import { FC, MouseEvent as ReactMouseEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { captureException } from '@sentry/react';
import { Editor } from '@tiptap/react';
import { BubbleMenu } from '@tiptap/react/menus';
import { Button, Popover } from 'antd';
import { TbMessage2 } from 'react-icons/tb';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import { useResourceCommentMutation } from '@/hooks/useResourceCommentMutation';
import { resourceCommentService } from '@/services/database';
import {
    emitResourceActivityOpenEvent,
    getOptimisticResourceComments,
    subscribeResourceCommentEvents,
    subscribeResourceCommentTargetFocusEvents,
} from '@/shared/components/activity/resourceCommentEvents';
import type { CommentComposerValue, ResourceActivityResourceType, ResourceComment } from '@/types';

import { memoCommentHighlightPluginKey, MemoCommentHighlightRange } from '../extensions/MemoCommentHighlight';
import EditorInlineFormattingControls from './EditorInlineFormattingControls';
import MemoCommentComposer from './MemoCommentComposer';
import MemoCommentHoverCard from './MemoCommentHoverCard';
import {
    childCommentsFor,
    commentHighlightRangeFromComment,
    getCommentHoverCardPosition,
    getCommentHoverTargetForComment,
    getCommentHoverTargetFromElement,
    getEditorSelectionState,
    getMemoCommentHoverElementFromEventTarget,
    getMemoCommentTarget,
    MemoCommentHoverCardPosition,
    MemoCommentTarget,
    numericTargetValue,
    upsertComment,
} from './memoCommentUtils';

interface EditorPopoverProps {
    canComment?: boolean;
    editor: Editor;
    onCommentCreated?: (comment: ResourceComment) => void;
    resourceId?: string;
    resourceType?: ResourceActivityResourceType;
}

const EditorPopover: FC<EditorPopoverProps> = ({
    canComment = true,
    editor,
    onCommentCreated,
    resourceId,
    resourceType = 'memos',
}): JSX.Element => {
    const [commentOpen, setCommentOpen] = useState(false);
    const [commentTarget, setCommentTarget] = useState<MemoCommentTarget>();
    const [selectionState, setSelectionState] = useState(() => getEditorSelectionState(editor));
    const [memoComments, setMemoComments] = useState<ResourceComment[]>([]);
    const [hoveredCommentId, setHoveredCommentId] = useState<string>();
    const [hoverCardPosition, setHoverCardPosition] = useState<MemoCommentHoverCardPosition>();
    const commentTargetRef = useRef<MemoCommentTarget | undefined>(undefined);
    const hideHoverCardTimeoutRef = useRef<number | undefined>(undefined);

    const { token } = useAntDesignTheme();
    const { createComment: persistComment, submitting: commentSubmitting } = useResourceCommentMutation({
        errorMessage: 'Could not create comment',
        resourceId: resourceId ?? '',
        resourceType,
    });
    const { createComment: persistReply, submitting: hoverReplySubmitting } = useResourceCommentMutation({
        errorMessage: 'Could not create reply',
        resourceId: resourceId ?? '',
        resourceType,
    });

    const hoveredComment = useMemo(
        () => memoComments.find((comment) => comment.id === hoveredCommentId && !comment.parentId),
        [hoveredCommentId, memoComments],
    );
    const hoveredCommentReplies = useMemo(
        () => (hoveredComment ? childCommentsFor(memoComments, hoveredComment.id) : []),
        [hoveredComment, memoComments],
    );
    const bubbleMenuOptions = useMemo(() => ({ placement: 'top-start' as const }), []);

    const dispatchMemoCommentMeta = useCallback(
        (meta: Record<string, unknown>) => {
            if (!editor || editor.isDestroyed) {
                return;
            }

            editor.view.dispatch(editor.state.tr.setMeta(memoCommentHighlightPluginKey, meta));
        },
        [editor],
    );

    const clearDraftCommentHighlight = useCallback(() => {
        dispatchMemoCommentMeta({ type: 'clearDraft' });
    }, [dispatchMemoCommentMeta]);

    const addPersistedCommentHighlight = useCallback(
        (comment: ResourceComment) => {
            const range = commentHighlightRangeFromComment(comment);

            if (!range) {
                return;
            }

            dispatchMemoCommentMeta({
                ...range,
                type: 'add',
            });
        },
        [dispatchMemoCommentMeta],
    );

    const removePersistedCommentHighlight = useCallback(
        (commentId: string) => {
            dispatchMemoCommentMeta({
                commentId,
                type: 'remove',
            });
        },
        [dispatchMemoCommentMeta],
    );

    const replacePersistedCommentHighlights = useCallback(
        (comments: ResourceComment[]) => {
            const highlights = comments.reduce<MemoCommentHighlightRange[]>((acc, comment) => {
                if (comment.parentId) {
                    return acc;
                }

                const range = commentHighlightRangeFromComment(comment);

                if (range) {
                    acc.push(range);
                }

                return acc;
            }, []);

            dispatchMemoCommentMeta({
                highlights,
                type: 'replace',
            });
        },
        [dispatchMemoCommentMeta],
    );

    const applyDraftCommentHighlight = useCallback(
        (target: MemoCommentTarget) => {
            const metadataTarget = (target.metadata?.target ?? {}) as Record<string, unknown>;
            const from = numericTargetValue(metadataTarget, 'from');
            const to = numericTargetValue(metadataTarget, 'to');

            if (from === undefined || to === undefined || from >= to) {
                clearDraftCommentHighlight();
                return;
            }

            dispatchMemoCommentMeta({
                from,
                to,
                type: 'setDraft',
            });
        },
        [clearDraftCommentHighlight, dispatchMemoCommentMeta],
    );

    const captureCommentTarget = useCallback(() => {
        const target = getMemoCommentTarget(editor);

        setCommentTarget(target);

        return target;
    }, [editor]);

    const openCommentComposerForTarget = useCallback(
        (target: MemoCommentTarget) => {
            commentTargetRef.current = target;
            setCommentTarget(target);
            applyDraftCommentHighlight(target);
            setCommentOpen(true);
        },
        [applyDraftCommentHighlight],
    );

    const openCommentComposerFromSelection = useCallback(() => {
        if (!resourceId || !canComment) {
            return;
        }

        const target = captureCommentTarget();

        if (!target) {
            return;
        }

        openCommentComposerForTarget(target);
    }, [canComment, captureCommentTarget, openCommentComposerForTarget, resourceId]);

    const closeCommentComposer = useCallback(() => {
        commentTargetRef.current = undefined;
        clearDraftCommentHighlight();
        setCommentOpen(false);
        setCommentTarget(undefined);
    }, [clearDraftCommentHighlight]);

    const openCommentComposer = (event: ReactMouseEvent<HTMLElement>) => {
        event.preventDefault();
        event.stopPropagation();
        openCommentComposerFromSelection();
    };

    useEffect(() => {
        const handleSelectionChange = () => setSelectionState(getEditorSelectionState(editor));

        editor.on('selectionUpdate', handleSelectionChange);
        editor.on('transaction', handleSelectionChange);

        return () => {
            editor.off('selectionUpdate', handleSelectionChange);
            editor.off('transaction', handleSelectionChange);
        };
    }, [editor]);

    useEffect(() => {
        const handleKeyDown = (event: KeyboardEvent) => {
            if (
                !editor.isFocused ||
                !event.ctrlKey ||
                !event.shiftKey ||
                event.metaKey ||
                event.altKey ||
                event.key.toLowerCase() !== 'm'
            ) {
                return;
            }

            event.preventDefault();
            event.stopPropagation();
            openCommentComposerFromSelection();
        };

        document.addEventListener('keydown', handleKeyDown, true);

        return () => document.removeEventListener('keydown', handleKeyDown, true);
    }, [editor, openCommentComposerFromSelection]);

    useEffect(() => {
        if (!resourceId) {
            return;
        }

        let cancelled = false;

        const loadCommentHighlights = async () => {
            try {
                const loadedComments = await resourceCommentService.list(resourceType, resourceId, {
                    includeResolved: true,
                });
                const comments = getOptimisticResourceComments(resourceType, resourceId).reduce(
                    upsertComment,
                    loadedComments,
                );

                if (!cancelled) {
                    setMemoComments(comments);
                    replacePersistedCommentHighlights(comments);
                }
            } catch (error) {
                captureException(error);
            }
        };

        loadCommentHighlights();

        return () => {
            cancelled = true;
        };
    }, [replacePersistedCommentHighlights, resourceId, resourceType]);

    useEffect(() => {
        return subscribeResourceCommentEvents((detail) => {
            if (detail.resourceType !== resourceType || detail.resourceId !== resourceId) {
                return;
            }

            if (detail.action === 'deleted' && detail.commentId) {
                setMemoComments((previous) =>
                    previous.filter(
                        (comment) => comment.id !== detail.commentId && comment.parentId !== detail.commentId,
                    ),
                );
                removePersistedCommentHighlight(detail.commentId);
                return;
            }

            if (detail.comment) {
                setMemoComments((previous) => upsertComment(previous, detail.comment as ResourceComment));

                if (!detail.comment.parentId) {
                    addPersistedCommentHighlight(detail.comment);
                }
            }
        });
    }, [addPersistedCommentHighlight, removePersistedCommentHighlight, resourceId, resourceType]);

    useEffect(() => {
        const handleHighlightClick = (event: globalThis.MouseEvent) => {
            const targetElement =
                event.target instanceof HTMLElement
                    ? event.target
                    : event.target instanceof Text
                      ? event.target.parentElement
                      : undefined;

            if (!targetElement || !resourceId) {
                return;
            }

            const highlight = targetElement.closest<HTMLElement>('[data-memo-comment-id]');
            const commentId = highlight?.dataset.memoCommentId;

            if (!commentId) {
                return;
            }

            event.preventDefault();
            event.stopPropagation();
            emitResourceActivityOpenEvent({
                commentId,
                resourceId,
                resourceType,
                tab: 'comments',
            });
        };

        const element = editor.view.dom;

        element.addEventListener('click', handleHighlightClick);

        return () => element.removeEventListener('click', handleHighlightClick);
    }, [editor, resourceId, resourceType]);

    const clearHoverCardHideTimeout = useCallback(() => {
        if (hideHoverCardTimeoutRef.current) {
            window.clearTimeout(hideHoverCardTimeoutRef.current);
            hideHoverCardTimeoutRef.current = undefined;
        }
    }, []);

    const scheduleHoverCardHide = useCallback(() => {
        clearHoverCardHideTimeout();

        hideHoverCardTimeoutRef.current = window.setTimeout(() => {
            setHoveredCommentId(undefined);
            setHoverCardPosition(undefined);
        }, 180);
    }, [clearHoverCardHideTimeout]);

    useEffect(() => {
        const element = editor.view.dom;

        const handleCommentTargetMouseOver = (event: globalThis.MouseEvent) => {
            const targetElement = getMemoCommentHoverElementFromEventTarget(event.target);
            const hoverTarget = targetElement
                ? getCommentHoverTargetFromElement(targetElement, memoComments, editor, element)
                : undefined;

            if (!hoverTarget) {
                return;
            }

            clearHoverCardHideTimeout();
            setHoveredCommentId(hoverTarget.commentId);
            setHoverCardPosition(getCommentHoverCardPosition(hoverTarget.commentId, hoverTarget.anchor, element));
        };

        const handleCommentTargetMouseOut = (event: globalThis.MouseEvent) => {
            const targetElement = getMemoCommentHoverElementFromEventTarget(event.target);
            const hoverTarget = targetElement
                ? getCommentHoverTargetFromElement(targetElement, memoComments, editor, element)
                : undefined;

            if (!hoverTarget) {
                return;
            }

            scheduleHoverCardHide();
        };

        element.addEventListener('mouseover', handleCommentTargetMouseOver);
        element.addEventListener('mouseout', handleCommentTargetMouseOut);

        return () => {
            element.removeEventListener('mouseover', handleCommentTargetMouseOver);
            element.removeEventListener('mouseout', handleCommentTargetMouseOut);
        };
    }, [clearHoverCardHideTimeout, editor, memoComments, scheduleHoverCardHide]);

    useEffect(() => {
        return () => clearHoverCardHideTimeout();
    }, [clearHoverCardHideTimeout]);

    useEffect(() => {
        if (!hoveredCommentId || hoverCardPosition) {
            return;
        }

        const comment = memoComments.find((item) => item.id === hoveredCommentId && !item.parentId);

        if (!comment) {
            return;
        }

        const editorRoot = editor.view.dom;
        const hoverTarget = getCommentHoverTargetForComment(comment, editor, editorRoot);

        if (hoverTarget) {
            setHoverCardPosition(getCommentHoverCardPosition(comment.id, hoverTarget.anchor, editorRoot));
        }
    }, [editor, hoverCardPosition, hoveredCommentId, memoComments]);

    useEffect(() => {
        return subscribeResourceCommentTargetFocusEvents((detail) => {
            if (detail.resourceType !== resourceType || detail.resourceId !== resourceId) {
                return;
            }

            const target = detail.target ?? {};
            const from = numericTargetValue(target, 'from');
            const to = numericTargetValue(target, 'to');
            const position = numericTargetValue(target, 'position');
            const attrs = (target.attrs ?? {}) as Record<string, unknown>;
            const reportComponentId =
                typeof target.componentId === 'string'
                    ? target.componentId
                    : typeof attrs.reportComponentId === 'string'
                      ? attrs.reportComponentId
                      : undefined;

            if (editor.isDestroyed) {
                return;
            }

            if (detail.commentId) {
                clearHoverCardHideTimeout();
                setHoveredCommentId(detail.commentId);
                setHoverCardPosition(undefined);
            }

            if (from !== undefined && to !== undefined && from < to) {
                editor.chain().focus().setTextSelection({ from, to }).run();
                if (detail.commentId) {
                    editor.view.dispatch(
                        editor.state.tr
                            .setMeta(memoCommentHighlightPluginKey, {
                                commentId: detail.commentId,
                                from,
                                to,
                                type: 'add',
                            })
                            .scrollIntoView(),
                    );
                } else {
                    editor.view.dispatch(editor.state.tr.scrollIntoView());
                }
                return true;
            }

            let targetPosition = position;

            if (reportComponentId) {
                editor.state.doc.descendants((node, pos) => {
                    if (
                        node.type.name === 'reportComponentEmbed' &&
                        node.attrs.reportComponentId === reportComponentId
                    ) {
                        targetPosition = pos;
                        return false;
                    }

                    return true;
                });
            }

            if (targetPosition !== undefined) {
                editor.chain().focus().setNodeSelection(targetPosition).run();
                editor.view.dispatch(editor.state.tr.scrollIntoView());
                return true;
            }
        });
    }, [clearHoverCardHideTimeout, editor, resourceId, resourceType]);

    useEffect(() => {
        return () => clearDraftCommentHighlight();
    }, [clearDraftCommentHighlight]);

    const createComment = async ({ mentions, message }: CommentComposerValue) => {
        const target = commentTargetRef.current ?? commentTarget ?? captureCommentTarget();

        if (!message || !resourceId || !canComment || !target) {
            return false;
        }

        const input = {
            component: target.component,
            mentions,
            message,
            metadata: target.metadata,
        };
        const comment = await persistComment(input, {
            onError: () => {
                setCommentOpen(true);
                commentTargetRef.current = target;
                setCommentTarget(target);
                applyDraftCommentHighlight(target);
            },
            onOptimisticCreated: (optimisticComment) => {
                onCommentCreated?.(optimisticComment);
                clearDraftCommentHighlight();
                addPersistedCommentHighlight(optimisticComment);
                setCommentOpen(false);
                commentTargetRef.current = undefined;
                setCommentTarget(undefined);
            },
        });

        return Boolean(comment);
    };

    const createThreadReply = async ({ mentions, message }: CommentComposerValue) => {
        if (!resourceId || !canComment || !hoveredComment) {
            return false;
        }

        const input = {
            component: hoveredComment.component,
            mentions,
            message,
            metadata: {
                source: 'memo-comment-hover-card',
                target: {
                    parentCommentId: hoveredComment.id,
                    type: 'comment-reply',
                },
            },
            parentId: hoveredComment.id,
        };
        return Boolean(await persistReply(input));
    };

    const hoverCard =
        hoveredComment && hoverCardPosition ? (
            <MemoCommentHoverCard
                canComment={canComment}
                comment={hoveredComment}
                onMouseEnter={clearHoverCardHideTimeout}
                onMouseLeave={scheduleHoverCardHide}
                onReplySubmit={createThreadReply}
                position={hoverCardPosition}
                replies={hoveredCommentReplies}
                replySubmitting={hoverReplySubmitting}
                resourceId={resourceId}
            />
        ) : null;

    const commentComposer = (
        <MemoCommentComposer
            disabled={!resourceId || !canComment}
            onSubmit={createComment}
            resourceId={resourceId ?? ''}
            resourceType={resourceType}
            submitting={commentSubmitting}
            target={commentTarget}
        />
    );

    return (
        <>
            {hoverCard}
            {selectionState.hasSelectedText && (
                <BubbleMenu className='bubble-menu' updateDelay={100} options={bubbleMenuOptions} editor={editor}>
                    <div
                        style={{
                            backgroundColor: token.colorBgContainer,
                            border: `1px solid ${token.colorBorder}`,
                            borderRadius: token.borderRadius,
                            maxWidth: 400,
                        }}
                    >
                        <EditorInlineFormattingControls
                            editor={editor}
                            canUseInlineControls={selectionState.canUseInlineControls}
                        />
                        <Popover
                            arrow={false}
                            destroyOnHidden
                            trigger='click'
                            placement='bottomLeft'
                            open={commentOpen}
                            onOpenChange={(open) => {
                                if (open) {
                                    const target = commentTargetRef.current ?? commentTarget;

                                    if (target) {
                                        openCommentComposerForTarget(target);
                                    } else {
                                        openCommentComposerFromSelection();
                                    }
                                    return;
                                }

                                closeCommentComposer();
                            }}
                            content={commentComposer}
                            styles={{ body: { padding: 0 } }}
                        >
                            <Button
                                disabled={!canComment || !resourceId}
                                onMouseDown={openCommentComposer}
                                onClick={(event) => event.preventDefault()}
                                icon={<TbMessage2 size='1rem' style={{ verticalAlign: '-0.125rem' }} />}
                                style={{
                                    borderTopRightRadius: 0,
                                    borderBottomRightRadius: 0,
                                    borderRightColor: 'transparent',
                                }}
                                type='text'
                            />
                        </Popover>
                    </div>
                </BubbleMenu>
            )}
        </>
    );
};

export default EditorPopover;
